/**
 * ProfilePhotoUpload Component
 *
 * A comprehensive profile photo upload component with cropping functionality using react-avatar-editor.
 * Supports JPEG, PNG, and WebP formats with a 5MB file size limit.
 * Features drag-and-drop upload, image cropping, and preview functionality.
 *
 * @module components/ui/ProfilePhotoUpload
 */

import React, { useState, useRef, useCallback } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * ProfilePhotoUpload component props interface
 */
export interface ProfilePhotoUploadProps {
  /** Current photo URL or base64 string */
  value?: string;
  /** Change handler for photo upload */
  onChange: (photoData: string | null) => void;
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Size of the upload area */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Supported file types
 */
const SUPPORTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * ProfilePhotoUpload component with react-avatar-editor cropping functionality
 *
 * @example
 * ```tsx
 * <ProfilePhotoUpload
 *   value={profilePhoto}
 *   onChange={setProfilePhoto}
 *   error={photoError}
 * />
 * ```
 */
export const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  className,
  size = 'md'
}) => {
  const logger = getLogger();
  const { isDark } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | File | null>(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0.5, y: 0.5 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<AvatarEditor>(null);

  // Size configurations
  const sizeConfig = {
    sm: {
      container: 'w-24 h-24',
      preview: 'w-20 h-20',
      upload: 'w-24 h-24',
      editor: { width: 200, height: 200 }
    },
    md: {
      container: 'w-32 h-32',
      preview: 'w-28 h-28',
      upload: 'w-32 h-32',
      editor: { width: 250, height: 250 }
    },
    lg: {
      container: 'w-40 h-40',
      preview: 'w-36 h-36',
      upload: 'w-40 h-40',
      editor: { width: 300, height: 300 }
    }
  };

  /**
   * Validate file type and size
   */
  const validateFile = (file: File): string | null => {
    if (!SUPPORTED_TYPES.includes(file.type)) {
      return 'Please upload a JPEG, PNG, or WebP image file.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 5MB.';
    }
    return null;
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      logger.error(validationError);
      return;
    }

    setIsUploading(true);
    setOriginalImage(file);
    setShowCropper(true);
    setScale(1);
    setPosition({ x: 0.5, y: 0.5 });
    setIsUploading(false);
  }, []);

  /**
   * Handle drag events
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [disabled, handleFileSelect]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  /**
   * Handle scale change
   */
  const handleScaleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setScale(parseFloat(e.target.value));
  }, []);


  /**
   * Handle position change from avatar editor
   */
  const handlePositionChange = useCallback((position: { x: number; y: number }) => {
    setPosition(position);
  }, []);

  /**
   * Apply crop and generate final image
   */
  const applyCrop = useCallback(() => {
    if (!editorRef.current) return;

    try {
      const canvas = editorRef.current.getImageScaledToCanvas();
      const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
      onChange(croppedImage);
      setShowCropper(false);
      setOriginalImage(null);
    } catch (error) {
      logger.error('Error applying crop:', error);
    }
  }, [onChange]);

  /**
   * Cancel cropping
   */
  const cancelCrop = useCallback(() => {
    setShowCropper(false);
    setOriginalImage(null);
    setScale(1);
    setPosition({ x: 0.5, y: 0.5 });
  }, []);

  /**
   * Remove photo
   */
  const removePhoto = useCallback(() => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange]);

  /**
   * Open file dialog
   */
  const openFileDialog = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  return (
    <div className={cn('relative', className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Main upload area */}
      <div className="flex flex-col items-center space-y-4">
        {/* Photo preview or upload area */}
        <div
          className={cn(
            'relative rounded-full border-2 border-dashed transition-all duration-200 cursor-pointer',
            sizeConfig[size].container,
            {
              [isDragging && !disabled ? (isDark ? 'border-primary-600 bg-primary-900/20' : 'border-primary-300 bg-primary-50') : '']: isDragging && !disabled,
              [!isDragging && !error && !disabled ? (isDark ? 'border-secondary-600' : 'border-secondary-300') : '']: !isDragging && !error && !disabled,
              [error ? (isDark ? 'border-red-600' : 'border-red-300') : '']: error,
              [disabled ? (isDark ? 'border-secondary-700' : 'border-secondary-200') : '']: disabled,
              'opacity-50 cursor-not-allowed': disabled,
              [!disabled && !error ? (isDark ? 'hover:border-primary-500' : 'hover:border-primary-400') : '']: !disabled && !error
            }
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          {value ? (
            // Photo preview - centered in the upload area
            <div className="flex items-center justify-center w-full h-full">
              <div className={cn('relative rounded-full overflow-hidden', sizeConfig[size].preview)}>
                <img
                  src={value}
                  alt="Profile photo"
                  className="w-full h-full object-cover"
                />
                {!disabled && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Upload placeholder
            <div className={cn('flex flex-col items-center justify-center text-center p-4', sizeConfig[size].upload)}>
              {isUploading ? (
                <svg className="w-8 h-8 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <>
                  <svg className={cn(
                    "w-8 h-8 mb-2",
                    isDark ? "text-secondary-500" : "text-secondary-400"
                  )} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-xs text-secondary-600 dark:text-secondary-400">
                    Upload Photo
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Upload instructions */}
        <div className="text-center">
          <p className={cn(
            "text-sm",
            isDark ? "text-secondary-400" : "text-secondary-600"
          )}>
            {value ? 'Click to change photo' : 'Click or drag to upload'}
          </p>
          <p className="text-xs text-secondary-500 mt-1">
            JPEG, PNG, or WebP • Max 5MB
          </p>
        </div>

        {/* Remove button */}
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removePhoto();
            }}
            className={cn(
              "text-sm transition-colors",
              isDark
                ? "text-red-400 hover:text-red-300"
                : "text-red-600 hover:text-red-700"
            )}
          >
            Remove Photo
          </button>
        )}

        {/* Error message */}
        {error && (
          <p className={cn(
            "text-sm text-center",
            isDark ? "text-red-400" : "text-red-600"
          )}>
            {error}
          </p>
        )}
      </div>

      {/* Cropping modal */}
      {showCropper && originalImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className={cn(
            "rounded-lg p-6 max-w-lg w-full mx-4",
            isDark ? "bg-secondary-800" : "bg-white"
          )}>
            <h3 className={cn(
              "text-lg font-semibold mb-4",
              isDark ? "text-secondary-100" : "text-secondary-900"
            )}>
              Crop Your Photo
            </h3>

            {/* Avatar Editor */}
            <div className="flex justify-center mb-4">
              <AvatarEditor
                ref={editorRef}
                image={originalImage}
                width={sizeConfig[size].editor.width}
                height={sizeConfig[size].editor.height}
                border={20}
                borderRadius={sizeConfig[size].editor.width / 2} // Make it circular
                color={[0, 0, 0, 0.6]} // Semi-transparent black overlay
                scale={scale}
                position={position}
                onPositionChange={handlePositionChange}
                className="rounded-full"
              />
            </div>

            {/* Controls */}
            <div className="space-y-4 mb-4">
              {/* Scale control */}
              <div>
                <label className={cn(
                  "block text-sm font-medium mb-2",
                  isDark ? "text-secondary-200" : "text-secondary-700"
                )}>
                  Zoom: {scale.toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={scale}
                  onChange={handleScaleChange}
                  className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer dark:bg-secondary-700"
                />
              </div>

            </div>

            <p className={cn(
              "text-sm mb-4 text-center",
              isDark ? "text-secondary-400" : "text-secondary-600"
            )}>
              Drag to reposition and use zoom control for the perfect profile picture
            </p>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={cancelCrop}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                  isDark
                    ? "text-secondary-300 bg-secondary-700 hover:bg-secondary-600"
                    : "text-secondary-700 bg-secondary-100 hover:bg-secondary-200"
                )}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyCrop}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Apply Crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePhotoUpload;